import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import EmergencyBanner from '@/pages/home/components/EmergencyBanner';
import { servicesList } from '@/mocks/services';
import SeoHead from '@/components/feature/SeoHead';

const STEPS = [
  { icon: 'ri-phone-fill', step: '1', titleKey: 'sp.step.1.title', descKey: 'sp.step.1.desc' },
  { icon: 'ri-map-pin-fill', step: '2', titleKey: 'sp.step.2.title', descKey: 'sp.step.2.desc' },
  { icon: 'ri-price-tag-3-fill', step: '3', titleKey: 'sp.step.3.title', descKey: 'sp.step.3.desc' },
  { icon: 'ri-tools-fill', step: '4', titleKey: 'sp.step.4.title', descKey: 'sp.step.4.desc' },
];

const HIZMETLER_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz'}/` },
      { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz'}/hizmetler` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Tesisat Hizmetleri | Van Polat Tesisat',
    description:
      'Van\'da profesyonel tesisat hizmetleri: su kaçağı tespiti, tıkanıklık açma, kombi servisi, petek temizleme ve daha fazlası.',
    url: `${import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz'}/hizmetler`,
    inLanguage: 'tr-TR',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Tesisat Hizmetleri',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Van Polat Tesisat',
      url: `${import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz'}/`,
      telephone: '+905076074165',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Van',
        addressRegion: 'Van',
        addressCountry: 'TR',
      },
    },
    areaServed: { '@type': 'City', name: 'Van' },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Tesisat Hizmetleri',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Su Kaçağı Tespiti' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Tıkanıklık Açma' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kombi Servisi' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Petek Temizleme' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lavabo Açma' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kanal Açma' } },
      ],
    },
  },
];

export default function Hizmetler() {
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead
        title="Tesisat Hizmetleri | Van Polat Tesisat"
        description="Van'da profesyonel tesisat hizmetleri: su kaçağı tespiti, tıkanıklık açma, kombi servisi, petek temizleme, lavabo ve kanal açma. 7/24 acil tesisat hizmeti."
        keywords="van tesisat hizmetleri, su kaçağı tespiti van, tıkanıklık açma van, kombi servisi van, petek temizleme van, lavabo açma van, kanal açma van"
        canonical="/hizmetler"
        schema={HIZMETLER_SCHEMA}
      />
      <Navbar />
      <EmergencyBanner />

      {/* ─── Hero ─── */}
      <section className="relative pt-24 pb-10 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e0fa86b103313ee8d64687f6d13413cd.jpg"
            alt="Hizmetler"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/92 via-blue-800/85 to-blue-700/75" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
            <i className="ri-settings-3-fill text-blue-200 text-xs"></i>
            {t('sp.hero.badge')}
          </span>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('sp.hero.title')}
          </h1>
          <p className="text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto mb-7">{t('sp.hero.sub')}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href="tel:+905076074165"
              className="flex items-center justify-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-extrabold text-sm px-6 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i> {t('nav.call')} · 0507 607 4165
            </a>
            <a href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum."
              target="_blank" rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-extrabold text-sm px-6 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line"></i> {t('nav.whatsapp')}
            </a>
          </div>
        </div>
      </section>

      {/* ─── Services Grid ─── */}
      <section className="py-10 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-7 md:mb-12">
            <h2 className="text-xl sm:text-3xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('sp.grid.title')} <span className="text-blue-600">{t('sp.grid.highlight')}</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">{t('sp.grid.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {servicesList.map((svc) => (
              <article key={svc.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-blue-200 transition-all flex flex-col">
                {/* Image */}
                <Link to={`/hizmetler/${svc.slug}`} className="w-full h-48 sm:h-44 overflow-hidden shrink-0 block cursor-pointer">
                  <img src={svc.image} alt={t(`svc.${svc.id}.title`)} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                </Link>
                {/* Body */}
                <div className="p-4 sm:p-5 flex flex-col flex-1">
                  {/* Icon + title */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center bg-blue-50 rounded-xl">
                      <i className={`${svc.icon} text-blue-600 text-lg`}></i>
                    </span>
                    <h3 className="font-extrabold text-gray-900 text-sm leading-snug" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t(`svc.${svc.id}.title`)}
                    </h3>
                  </div>
                  {/* Description */}
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">
                    {t(`svc.${svc.id}.desc`)}
                  </p>
                  {/* Buttons */}
                  <div className="flex gap-2 mb-2">
                    <a href="tel:+905076074165"
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-phone-fill"></i> Hemen Ara
                    </a>
                    <a href={svc.waUrl} target="_blank" rel="nofollow noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-sm font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                      title={`WhatsApp — ${t(`svc.${svc.id}.title`)}`}>
                      <i className="ri-whatsapp-line"></i> WhatsApp
                    </a>
                  </div>
                  <Link
                    to={`/hizmetler/${svc.slug}`}
                    className="flex items-center justify-center gap-2 border border-blue-200 text-blue-600 hover:bg-blue-50 text-sm font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-information-line"></i> Detaylı Bilgi
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Process ─── */}
      <section className="py-10 md:py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl sm:text-3xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('sp.process.title')} <span className="text-blue-600">{t('sp.process.highlight')}</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">{t('sp.process.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 sm:p-5 text-center border border-blue-100">
                <div className="relative w-12 h-12 mx-auto mb-3">
                  <span className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full">
                    <i className={`${s.icon} text-white text-xl`}></i>
                  </span>
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-700 text-xs font-extrabold rounded-full">
                    {s.step}
                  </span>
                </div>
                <p className="font-extrabold text-gray-900 text-xs sm:text-sm mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>{t(s.titleKey)}</p>
                <p className="text-gray-500 text-xs leading-relaxed">{t(s.descKey)}</p>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-10 bg-blue-700 rounded-2xl p-6 text-center">
            <p className="text-white font-extrabold text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>Hemen Yardım Alın!</p>
            <p className="text-blue-200 text-sm mb-5">Van ve çevre ilçelerde aynı gün hizmet.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a href="tel:+905076074165"
                className="flex items-center justify-center gap-2 bg-white text-blue-700 font-extrabold text-sm px-6 py-3.5 rounded-xl cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill"></i> 0507 607 4165
              </a>
              <a href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum."
                target="_blank" rel="nofollow noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-extrabold text-sm px-6 py-3.5 rounded-xl cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line"></i> WhatsApp ile Yaz
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
